import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, KeyRound, ArrowLeft, MailCheck } from "lucide-react";
import { formatApiError } from "../contexts/AuthContext";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden bg-ink">
        <img
          src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="relative z-10 self-end p-12 text-cream max-w-md">
          <p className="text-[10px] uppercase tracking-[0.32em] text-accent font-bold mb-6">— EventMarket Pro</p>
          <h1 className="font-heading font-light text-5xl leading-tight tracking-editorial mb-4">
            Mot de passe<br /><em className="font-display text-accent">oublié ?</em>
          </h1>
          <p className="text-sm text-cream/70 leading-relaxed">
            Nous générons un mot de passe provisoire que vous pourrez changer immédiatement après connexion.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:px-16">
        <div className="w-full max-w-md">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="inline-flex items-center justify-center h-12 w-12 bg-trust/10 rounded-full mb-6">
                <MailCheck className="h-5 w-5 text-trust" />
              </div>
              <h1 className="font-heading font-light text-3xl text-ink mb-3">Email envoyé</h1>
              <p className="text-muted-foreground text-sm leading-relaxed mb-8 max-w-sm mx-auto">
                Si un compte EventMarket Pro est associé à <strong className="text-ink">{email}</strong>, vous allez recevoir un mot de passe provisoire (valable 30 minutes). Pensez à vérifier votre dossier spam.
              </p>
              <Link
                to="/login"
                data-testid="back-to-login"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider bg-ink text-cream hover:bg-ink/85 rounded-none"
              >
                <ArrowLeft className="h-3 w-3" /> Retour à la connexion
              </Link>
            </div>
          ) : (
            <>
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-ink mb-8"
                data-testid="forgot-back-link"
              >
                <ArrowLeft className="h-3 w-3" /> Retour à la connexion
              </Link>
              <div className="flex items-center gap-2 mb-3">
                <KeyRound className="h-5 w-5 text-accent" />
                <p className="text-label text-muted-foreground">Récupération de compte</p>
              </div>
              <h1 className="font-heading font-light text-4xl text-ink mb-3">
                Réinitialiser
              </h1>
              <p className="text-muted-foreground text-sm mb-8 leading-relaxed">
                Entrez l&apos;email associé à votre compte. Nous vous enverrons un mot de passe provisoire valable 30 minutes.
              </p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-password-email"
                    className="mt-1"
                  />
                </div>
                {error && (
                  <p className="text-sm text-destructive" data-testid="forgot-password-error">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-submit"
                  className="w-full bg-ink hover:bg-ink/85 text-cream rounded-none py-6"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Recevoir un mot de passe provisoire"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
